import {
    ActionButton,
    DirectionalHint,
    IContextualMenuItem,
    IDialogContentProps,
    Icon,
    IconButton,
    TooltipDelay,
    TooltipHost
} from "office-ui-fabric-react";
import React, { useMemo, useState } from "react";
import { EntityType, entityTypeLabelMap } from "../../entities/common";
import {
    CreateTimeTrackingEntryAttr,
    TimeTrackingEntry,
    TimeTrackingLine,
    TimelineToCreateTimeEntry,
    TimesheetLineStatus
} from "../../store/TimeTrackingStore";
import { notUndefined } from "../utils/common";
import { TimeTrackingGlobalSettings } from "../../store/Tenant";
import RemoveDialog from "../common/RemoveDialog";
import { ConfirmationDialog } from "../common/ConfirmationDialog";
import { getDaysBetweenDates } from "../common/timeline/utils";
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import { ShowO365Connector, TimeType, TimeTrackingReadonly } from "./common";
import { findPeriodForDate, isPeriodOpenForReporting, TimeReportingPeriod } from "../../store/TimeReportingPeriodsStore";

type OwnProps = {
    timeTrackingLine: TimeTrackingLine;
    entityType?: EntityType;
    readonly: TimeTrackingReadonly;

    onClearTimeEntries: (toClear: TimeTrackingEntry[]) => void;
    onDeleteTimeEntries: (ids: string[]) => void;

    onAddTime: (type: TimeType) => void;

    onCopyTask: (toCopy: CreateTimeTrackingEntryAttr[]) => void;

    onRecallLine?: (lineId: string, projectName: string, isAdministrative: boolean) => void;
};

type StateProps = {
    settings: TimeTrackingGlobalSettings;
    showO365Connector: boolean;
    shownPeriod: TimeReportingPeriod | null;
    periods: TimeReportingPeriod[];
};

type Props = OwnProps & StateProps;

function TimeTrackingItemMenu(props: Props) {
    const {
        timeTrackingLine,
        entityType,
        settings,
        readonly,
        onClearTimeEntries,
        onDeleteTimeEntries,
        onAddTime,
        showO365Connector,
        shownPeriod,
        periods,
        onRecallLine
    } = props;

    const [entriesToClear, setEntriesToClear] = useState<TimeTrackingEntry[] | undefined>();
    const [entriesToDelete, setEntriesToDelete] = useState<TimeTrackingEntry[] | undefined>();
    const [showRecallDialog, setShowRecallDialog] = useState(false);

    if (readonly.global) {
        return <></>;
    }

    const isOpenPeriod = isPeriodOpenForReporting(shownPeriod);
    const nextPeriod = useMemo(() => {
        if (!shownPeriod) {
            return null;
        }

        return findPeriodForDate(shownPeriod.attributes.EndDate.clone().addDays(1), periods);
    }, [shownPeriod, periods]);

    const isEditingDisabled = readonly.approval || readonly.projectDisabled;

    const commands: IContextualMenuItem[] = [];

    const entityTypeLabel = entityType
        ? timeTrackingLine.attributes.IsAdministrative
            ? entityType === EntityType.Project
                ? "Administrative Categories"
                : timeTrackingLine.attributes.Task.name
            : entityTypeLabelMap[entityType].singular
        : "";

    if (
        entityType === EntityType.Project &&
        isOpenPeriod &&
        onRecallLine &&
        timeTrackingLine.attributes.TimesheetLine?.status === TimesheetLineStatus.PendingApproval
    ) {
        commands.push({
            key: "recall",
            text: "Recall",
            iconProps: { iconName: "Undo" },
            onClick: () => setShowRecallDialog(true)
        });
    }

    if (entityType === EntityType.Task || entityType === EntityType.Project) {
        if (
            entityType === EntityType.Project &&
            !timeTrackingLine.attributes.IsAdministrative &&
            isOpenPeriod &&
            !isEditingDisabled &&
            !timeTrackingLine.attributes.ProjectDetails?.isDeleted &&
            !timeTrackingLine.attributes.ProjectDetails?.isArchived
        ) {
            commands.push({
                key: "import",
                text: `Import Task`,
                iconProps: { iconName: "PPMXImport" },
                onClick: () => onAddTime(TimeType.ProjectTask)
            });

            if (settings.enableReportingOnCustomTimeTrackingEntries) {
                commands.push({
                    key: "addCustomEvent",
                    name: "Add Custom Time Entry",
                    iconProps: { iconName: "Add" },
                    onClick: () => onAddTime(TimeType.CustomEvent)
                });
            }

            if (showO365Connector) {
                commands.push({
                    key: "importFromM365",
                    name: "Import from M365",
                    iconProps: { iconName: "M365Logo" },
                    onClick: () => onAddTime(TimeType.O365)
                });
            }
        }

        if (
            entityType === EntityType.Task &&
            !timeTrackingLine.attributes.ProjectDetails?.isDeleted &&
            !timeTrackingLine.attributes.TaskDetails?.isDeleted &&
            !timeTrackingLine.attributes.ProjectDetails?.isArchived &&
            !timeTrackingLine.attributes.TaskDetails?.isArchived &&
            shownPeriod &&
            isPeriodOpenForReporting(nextPeriod) &&
            !readonly.projectDisabled
        ) {
            const datesToCopy = getDaysBetweenDates(nextPeriod!.attributes.StartDate, nextPeriod!.attributes.EndDate);

            if (datesToCopy.length) {
                commands.push({
                    key: "copy",
                    text: `Copy ${entityTypeLabel} to the next Period`,
                    iconProps: {
                        iconName: "Copy"
                    },
                    onClick: () => {
                        const toCopy: CreateTimeTrackingEntryAttr[] = datesToCopy.map(_ =>
                            TimelineToCreateTimeEntry(timeTrackingLine, _, 0)
                        );
                        props.onCopyTask(toCopy);
                    }
                });
            }
        }

        if (isOpenPeriod && !isEditingDisabled) {
            const entriesWithReportedTime = timeTrackingLine.TimeEntries.filter(_ => _.attributes.Duration);

            if (entriesWithReportedTime.length) {
                commands.push({
                    key: "clear",
                    text: `Clear Visible ${entityTypeLabel} Time`,
                    iconProps: {
                        iconName: "Refresh"
                    },
                    onClick: () => {
                        setEntriesToClear(entriesWithReportedTime);
                    }
                });
            }

            if (!timeTrackingLine.attributes.SuggestionType) {
                const hasSuggestedTasks = timeTrackingLine.attributes.HasSuggestedTasks;

                commands.push({
                    key: "delete",
                    disabled: hasSuggestedTasks,
                    onRender: () => (
                        <div className="time-tracking-menu">
                            <div className={`ms-ContextualMenu-link align-center ${hasSuggestedTasks ? "is-disabled" : ""}`}>
                                <ActionButton
                                    className="ms-ContextualMenu-linkContent "
                                    onClick={() => setEntriesToDelete(timeTrackingLine.TimeEntries)}
                                    text={`Remove ${entityTypeLabel} from Timesheet`}
                                    style={
                                        hasSuggestedTasks
                                            ? undefined
                                            : {
                                                  color: "red"
                                              }
                                    }
                                    iconProps={{
                                        iconName: "Delete",
                                        style: hasSuggestedTasks
                                            ? undefined
                                            : {
                                                  color: "red"
                                              }
                                    }}
                                    disabled={hasSuggestedTasks}
                                />

                                {hasSuggestedTasks && (
                                    <TooltipHost
                                        content={
                                            entityType === EntityType.Project
                                                ? "You cannot delete this Project as its Tasks were suggested by the System."
                                                : `You cannot delete this ${entityTypeLabel} as it was suggested by the System`
                                        }
                                        delay={TooltipDelay.zero}
                                        hostClassName="tooltip"
                                        calloutProps={{ directionalHint: DirectionalHint.topCenter }}>
                                        <Icon iconName="Info" />
                                    </TooltipHost>
                                )}
                            </div>
                        </div>
                    )
                });
            }
        }
    }

    return (
        <>
            {!!commands.length && (
                <IconButton
                    menuIconProps={{ iconName: "PPMXMore" }}
                    menuProps={{
                        items: commands
                    }}
                />
            )}

            {!!entriesToClear?.length && (
                <RemoveDialog
                    dialogContentProps={_getClearTimeText(
                        timeTrackingLine,
                        entityTypeLabel,
                        entityType!,
                        settings,
                        entriesToClear
                    )}
                    onClose={() => {
                        setEntriesToClear(undefined);
                    }}
                    confirmButtonProps={{
                        text: "Clear"
                    }}
                    onComplete={() => {
                        onClearTimeEntries(entriesToClear);
                    }}
                />
            )}

            {!!entriesToDelete?.length && (
                <RemoveDialog
                    dialogContentProps={_getDeleteTimeText(
                        timeTrackingLine,
                        entityTypeLabel,
                        entityType!,
                        settings,
                        entriesToDelete
                    )}
                    onClose={() => {
                        setEntriesToDelete(undefined);
                    }}
                    onComplete={() => {
                        onDeleteTimeEntries(entriesToDelete.map(_ => _.id));
                    }}
                />
            )}

            {showRecallDialog && timeTrackingLine.attributes.TimesheetLine && (
                <ConfirmationDialog
                    onDismiss={() => setShowRecallDialog(false)}
                    onYes={() => {
                        onRecallLine?.(
                            timeTrackingLine.attributes.TimesheetLine!.id,
                            timeTrackingLine.attributes.Project.name,
                            !!timeTrackingLine.attributes.IsAdministrative
                        );
                    }}
                    onNo={() => {}}
                    dialogContentProps={{
                        title: "Recall Approval Request",
                        subText: timeTrackingLine.attributes.IsAdministrative
                            ? "Are you sure you want to recall the approval request for Administrative Time?\n\nThe approval request will be removed, and Administrative Time will return to the In Progress status."
                            : `Are you sure you want to recall the approval request for the project "${timeTrackingLine.attributes.Project.name}"?\n\nThe approval request will be removed, and the project will return to the In Progress status.`,
                        isMultiline: true
                    }}
                    yesButtonProps={{ text: "Recall" }}
                    noButtonProps={{ text: "Cancel" }}
                />
            )}
        </>
    );
}

const _getClearTimeText = (
    timeTrackingLine: TimeTrackingLine,
    entityTypeLabel: string,
    entityType: EntityType,
    setting: TimeTrackingGlobalSettings,
    timeEntriesToClear: TimeTrackingEntry[]
): IDialogContentProps => {
    const result: IDialogContentProps = {
        title: `Clear Visible ${entityTypeLabel} Time`,
        isMultiline: true
    };

    if (timeTrackingLine.attributes.IsAdministrative) {
        result.subText = `Are you sure you want to clear the ${entityTypeLabel} visible Reported Time?`;
    } else if (entityType === EntityType.Project) {
        const canRecalculateCompletedWork =
            setting.calculateCompletedWorkBasedOnReportedTime &&
            timeEntriesToClear.some(_ => !_.attributes.CustomTimeEntryName);

        result.subText = [
            `Are you sure you want to clear the Tasks visible Reported Time of the Project "${timeTrackingLine.attributes.Project.name}"?`,
            canRecalculateCompletedWork ? `Tasks Completed Work will be recalculated.` : undefined
        ]
            .filter(notUndefined)
            .join("\r\n");
    } else {
        const canRecalculateCompletedWork =
            setting.calculateCompletedWorkBasedOnReportedTime && !timeTrackingLine.attributes.IsCustomEvent;

        result.subText = [
            `Are you sure you want to clear the Task "${timeTrackingLine.attributes.Task.name}" visible Reported Time?`,
            canRecalculateCompletedWork ? `Task Completed Work will be recalculated.` : undefined
        ]
            .filter(notUndefined)
            .join("\r\n");
    }

    return result;
};

const _getDeleteTimeText = (
    timeTrackingLine: TimeTrackingLine,
    entityTypeLabel: string,
    entityType: EntityType,
    setting: TimeTrackingGlobalSettings,
    timeEntriesToDelete: TimeTrackingEntry[]
): IDialogContentProps => {
    const result: IDialogContentProps = {
        title: `Remove ${entityTypeLabel} from Timesheet`,
        isMultiline: true
    };

    if (timeTrackingLine.attributes.IsAdministrative) {
        result.subText = `Are you sure you want to remove the ${entityTypeLabel} with its Reported Time from the visible period of your Timesheet?`;
    } else if (entityType === EntityType.Project) {
        const canRecalculateCompletedWork =
            setting.calculateCompletedWorkBasedOnReportedTime &&
            timeEntriesToDelete.some(_ => !_.attributes.CustomTimeEntryName);

        result.subText = [
            `Are you sure you want to remove the Project "${timeTrackingLine.attributes.Project.name}" along with its Tasks and Reported Time from the visible period of your Timesheet?`,
            `Reported Time will be lost. ${canRecalculateCompletedWork ? `Tasks Completed Work will be recalculated.` : ""}`
        ]
            .filter(notUndefined)
            .join("\r\n");
    } else {
        const canRecalculateCompletedWork =
            setting.calculateCompletedWorkBasedOnReportedTime && !timeTrackingLine.attributes.IsCustomEvent;

        result.subText = [
            `Are you sure you want to remove the Task "${timeTrackingLine.attributes.Task.name}" along with its Reported Time from the visible period of your Timesheet?`,
            `Reported Time will be lost. ${canRecalculateCompletedWork ? `Task Completed Work will be recalculated.` : ""}`
        ]
            .filter(notUndefined)
            .join("\r\n");
    }

    return result;
};

function mapStateToProps(state: ApplicationState, ownProps: OwnProps): StateProps {
    return {
        settings: state.tenant.timeTracking.globalSettings,
        showO365Connector: ShowO365Connector(state),
        shownPeriod: state.timeTracking.shownPeriod,
        periods: state.timeReportingPeriods.periods
    };
}

export default connect(mapStateToProps)(TimeTrackingItemMenu);
